import {
  getPanelVisibilityFromDisplayState,
  initializePanelVisibility,
  isWorkbookFeatureForPanelAvailable,
  WorkbookPanelName
} from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { booleanFromSessionStorage } from 'cadenza/store/state-utils';
import { entries } from 'cadenza/utils/type-util';
import {
  type WorkbookState,
  slice,
  togglePanelVisibility,
  hideAllPanels
} from 'cadenza/workbook/workbook.state';
import { cadenzaSessionStorage } from 'cadenza/utils/storage/storage';

import type { Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';

interface PanelState {
  visible: boolean;
  disabled: boolean;
}

export type PanelById = Record<WorkbookPanelName, PanelState>;

const PANELS_PARAM = 'panels';

initializePanelVisibility(PANELS_PARAM); // side-effect!

export function createInitialPanelState () {
  const panelById =  Object.fromEntries(
    Object.values(WorkbookPanelName).map(panel => [ panel, {
      visible: booleanFromSessionStorage(panel, true),
      disabled: false
    } ])
  ) as PanelById;
  return { panelById };
}

export function persistPanelState (state: WorkbookState) {
  Object.values(WorkbookPanelName).forEach(panelId => {
    cadenzaSessionStorage.setItem(panelId, String(state.panelById[panelId].visible));
  });
}

// Reducers

export const panelReducers = {
  togglePanelVisibility: {
    prepare: (panelId: WorkbookPanelName, visible?: boolean) => ({ payload: { panelId, visible } }),
    reducer: (state, { payload: { panelId, visible = !state.panelById[panelId].visible } }: PayloadAction<{
      panelId: WorkbookPanelName;
      visible?: boolean;
    }>) => {
      const previousVisibility = isPanelVisible(state, panelId);
      state.panelById[panelId].visible = Boolean(visible);
      state.clientChanged = (isPanelVisible(state, panelId) !== previousVisibility);
    }
  },
  hideAllPanels (state) {
    Object.values(state.panelById).forEach(panel => { panel.visible = false; });
  }
} satisfies SliceCaseReducers<WorkbookState>;

// Helper reducers

export function updatePanelStateFromDisplayState (state: Draft<WorkbookState>) {
  const panelVisibility = getPanelVisibilityFromDisplayState(state.displayState);
  const workbookWasOpenedFromNavigator = (panelVisibility != null);
  if (workbookWasOpenedFromNavigator) {
    entries(panelVisibility).forEach(([ panelId, visible ]) => {
      state.panelById[panelId].visible = visible;
    });
  }
  for (const panelId of Object.values(WorkbookPanelName)) {
    state.panelById[panelId].visible = isWorkbookFeatureForPanelAvailable(panelId, state);
  }
}

export function forcePanelsHidden (state: Draft<WorkbookState>) {
  Object.values(WorkbookPanelName).forEach(panelId => {
    state.panelById[panelId] = { visible: false, disabled: true };
  });
}

// Action creators

export { togglePanelVisibility, hideAllPanels }; // Re-export to have all actions in this module

export const showPanel = (panelId: WorkbookPanelName) => togglePanelVisibility(panelId, true);
export const hidePanel = (panelId: WorkbookPanelName) => togglePanelVisibility(panelId, false);

// Selectors

export const isPanelVisible = (state: WorkbookState, panelId: WorkbookPanelName) => slice(state).panelById[panelId].visible;
export const isPanelDisabled = (state: WorkbookState, panelId: WorkbookPanelName) => slice(state).panelById[panelId].disabled;
